import loader from './loader2.gif';
import {
	LoaderDiv, PulseDot, PulseLoaderDiv, LoaderBar, LoaderProgress,
	SpinnerOuterRing, SpinnerMiddleRing, SpinnerInnerRing
} from './styles';
import LoadingSlider from './slider';
import { Interpolation } from 'styled-components';
import { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { RootState } from 'store';

/** Three-ring SVG equivalent of loader2.gif, used only in dark mode (see LoaderImage below).
 *  Rendered live via CSS vars/stroke-dashoffset instead of a GIF for two reasons: (1) the real
 *  GIF's anti-aliased ring edges are baked as fully-opaque near-white pixels (authored against a
 *  white canvas — confirmed by sampling the frame, e.g. rgba(255,244,219,255) right at the amber
 *  ring's inner edge), which shows as a visible white fringe on a dark background and can't be
 *  fixed in CSS since it's baked into the raster asset; (2) a from-scratch generated replacement
 *  GIF needs a reduced/dithered color palette, which read as visibly "scratchy" on the smooth
 *  anti-aliased ring edges — SVG has neither problem, rendering perfectly smooth at any size.
 *  Geometry/colors/timing below are pixel- and frame-measured directly off loader2.gif's real
 *  81-frame cycle (bright blue outer, dark navy middle, amber inner).
 *  RING_TRANSFORM: rotate(-90) alone grows clockwise from the fixed top anchor; the GIF actually
 *  grows counter-clockwise from that same anchor (confirmed by tracking the arc's leading edge
 *  angle across early frames: end fixed at the top, start angle decreasing — 359,353,345,334...).
 *  translate(0,100) scale(1,-1) mirrors the circle's path direction around its own center before
 *  the rotate, flipping growth to counter-clockwise while keeping the same fixed anchor point. */
const RING_TRANSFORM = 'rotate(-90 50 50) translate(0,100) scale(1,-1)';

function SpinnerIcon() {
	return (
		<svg className='loaderImage' viewBox='0 0 100 100' width='80' height='80'>
			<SpinnerOuterRing
				cx={50} cy={50} r={40} fill='none' stroke='var(--primary)' strokeWidth={6}
				strokeLinecap='butt' transform={RING_TRANSFORM}
			/>
			<SpinnerMiddleRing
				cx={50} cy={50} r={34} fill='none' stroke='var(--loaderAccentColor)' strokeWidth={6}
				strokeLinecap='butt' transform={RING_TRANSFORM}
			/>
			<SpinnerInnerRing
				cx={50} cy={50} r={28} fill='none' stroke='var(--secondary)' strokeWidth={6}
				strokeLinecap='butt' transform={RING_TRANSFORM}
			/>
		</svg>
	);
}

function LoaderImage() {
	const displayMode = useSelector((state: RootState) => state.QueryParams.data.displayMode);
	if (displayMode === 'dark') return <SpinnerIcon />;
	return <img className='loaderImage' src={loader} />;
}

export enum Loader_Type {
	SLIDER = 'slider',
	INDEFINITE = 'indefinite',
	BAR = 'bar',
	DEFAULT = 'default'
}

type Props = {
	loaderText?: string;
	type?: Loader_Type
	styles?: Interpolation<React.CSSProperties>;
	designType?: DesignType;
};

const ProgressStages = [
	{ text: 'Downloading imagery and lidar data', time: 5 },
	{ text: 'Processing the roof data', time: 5 },
	{ text: 'Creating facets and obstructions', time: 5 },
	{ text: 'Generating solar panel layout', time: 10 },
	{ text: 'Estimating energy production', time: 10 },
	{ text: 'Finalizing things...', time: 20 },
	{ text: 'This is taking longer than usual. Please wait for some time...', time: Infinity }
];

const ProgressStagesRoof = [
	{ text: 'Downloading imagery and lidar data', time: 5 },
	{ text: 'Processing the roof data', time: 5 },
	{ text: 'Creating facets', time: 8 },
	{ text: 'Finalizing things...', time: 20 },
	{ text: 'This is taking longer than usual. Please wait for some time...', time: Infinity },
];
function IndefiniteLoader({ designType }: { designType?: DesignType }) {

	const [currentStep, setCurrentStep] = useState<number>(0);
	const stepIndexRef = useRef<number>(0);
	const stages = designType === 'ROOF' ? ProgressStagesRoof : ProgressStages;

	useEffect(() => {
		let timeoutId: NodeJS.Timeout;
		const updateStep = () => {
			if (stepIndexRef.current < stages.length - 1) {
				setCurrentStep(stepIndexRef.current);
				timeoutId = setTimeout(updateStep, stages[stepIndexRef.current].time * 1000);
				stepIndexRef.current += 1;
			} else {
				setCurrentStep(stepIndexRef.current);
			}
		};

		updateStep();
		return () => {
			clearTimeout(timeoutId);
		};
	}, [stages]);

	return (
		<LoaderDiv>
			<p className='loaderText'>This will take a few seconds</p>
			<LoaderImage />
			<h3 className='loaderHeading'>{stages[currentStep].text}</h3>
			{/* <PulseLoaderDiv>

				<PulseDot delay="0s" />
				<PulseDot delay="0.2s" />
				<PulseDot delay="0.4s" />
			</PulseLoaderDiv> */}
		</LoaderDiv>
	);

}

export default function Loader(props: Props) {
	const { loaderText, styles, type = Loader_Type.DEFAULT, designType } = props;

	if (type === Loader_Type.INDEFINITE) {
		return <IndefiniteLoader designType={designType} />;
	}
	// BAR
	if (type === Loader_Type.BAR) {
		return (
			<LoaderDiv styles={styles}>
				<LoaderBar>
					<LoaderProgress />
				</LoaderBar>
			</LoaderDiv>
		);
	}

	return (
		<LoaderDiv styles={styles}>
			<LoaderImage />
			{loaderText && <p className='loaderText'>{loaderText}...</p>}
			{type === Loader_Type.SLIDER && <LoadingSlider />}
		</LoaderDiv>
	);
}
